import {
  useState, useRef, useCallback, type ReactNode 
} from 'react';
import {
  X, Sparkles, Loader2, Check, AlertCircle, Camera, Upload 
} from 'lucide-react';
import { Button } from './button';
import {
  streamGenAI, imageToBase64, type GenAIRequest 
} from '@/lib/genai-client';

type GenerationStatus = 'idle' | 'generating' | 'success' | 'error';

interface GenAIModalProps<T> {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onComplete: (result: T) => void;
  readonly promptKey: string;
  readonly title: string;
  readonly description: string;
  readonly placeholder: string;
  readonly supportsImage?: boolean;
  readonly contextBuilder?: () => Record<string, unknown>;
  readonly renderPreview?: (result: T) => ReactNode;
  readonly submitLabel?: string;
  readonly successLabel?: string;
}

export function GenAIModal<T>({
  isOpen,
  onClose,
  onComplete,
  promptKey,
  title,
  description,
  placeholder,
  supportsImage = false,
  contextBuilder,
  renderPreview,
  submitLabel = 'Generate',
  successLabel = 'Use Result',
}: GenAIModalProps<T>) {
  const [prompt, setPrompt] = useState('');
  const [status, setStatus] = useState<GenerationStatus>('idle');
  const [streamedContent, setStreamedContent] = useState('');
  const [result, setResult] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = () => setImagePreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleGenerate = useCallback(async () => {
    if (!prompt.trim() && !imageFile) return;

    setStatus('generating');
    setStreamedContent('');
    setResult(null);
    setError(null);

    abortControllerRef.current = new AbortController();

    const request: GenAIRequest = {
      promptKey,
      userPrompt: prompt.trim() || 'Analyze this image',
      context: contextBuilder?.(),
    };

    // Add image if provided
    if (imageFile) {
      try {
        request.image = await imageToBase64(imageFile);
      } catch {
        setError('Failed to process image');
        setStatus('error');
        return;
      }
    }

    await streamGenAI<T>(
      request,
      (chunk) => setStreamedContent((prev) => prev + chunk),
      (genResult) => {
        setResult(genResult);
        setStatus('success');
      },
      (err) => {
        setError(err.message);
        setStatus('error');
      },
      abortControllerRef.current.signal
    );
  }, [prompt, imageFile, promptKey, contextBuilder]);

  const handleCancel = () => {
    abortControllerRef.current?.abort();
    setStatus('idle');
  };

  const handleUseResult = () => {
    if (result) {
      onComplete(result);
      handleClose();
    }
  };

  const handleClose = () => {
    abortControllerRef.current?.abort();
    setPrompt('');
    setStreamedContent('');
    setResult(null);
    setError(null);
    setImageFile(null);
    setImagePreview(null);
    setStatus('idle');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-3xl max-h-[90vh] flex flex-col rounded-lg bg-background shadow-xl">
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between border-b px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-pink-500">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold">{title}</h2>
              <p className="text-sm text-muted-foreground">{description}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-full p-2 hover:bg-muted transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Image Upload (if supported) */}
          {supportsImage && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Image (optional)</label>
              <div className="flex items-center gap-4">
                {imagePreview ? (
                  <div className="relative">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="h-32 w-32 object-cover rounded-lg border"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setImageFile(null);
                        setImagePreview(null);
                      }}
                      className="absolute -top-2 -right-2 rounded-full bg-red-500 p-1 text-white"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={status === 'generating'}
                    className="flex h-32 w-32 flex-col items-center justify-center rounded-lg border-2 border-dashed border-muted-foreground/25 hover:border-muted-foreground/50 transition-colors"
                  >
                    <Camera className="h-8 w-8 text-muted-foreground mb-2" />
                    <span className="text-xs text-muted-foreground">Add Photo</span>
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                />
                {!imagePreview && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={status === 'generating'}
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    Upload Image
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Prompt Input */}
          <div className="space-y-2">
            <label htmlFor="genai-prompt" className="text-sm font-medium">
              {supportsImage ? 'Description (optional with image)' : 'Describe what you need'}
            </label>
            <textarea
              id="genai-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={placeholder}
              className="w-full h-32 rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              disabled={status === 'generating'}
            />
          </div>

          {/* Streaming Output */}
          {(status === 'generating' || streamedContent) && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                {status === 'generating' && (
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                )}
                {status === 'success' && (
                  <Check className="h-4 w-4 text-green-500" />
                )}
                <span className="text-sm font-medium">
                  {status === 'generating' ? 'Generating...' : 'Complete'}
                </span>
              </div>
              <div className="rounded-lg border bg-muted/50 p-4 max-h-48 overflow-y-auto">
                <pre className="text-xs whitespace-pre-wrap font-mono">
                  {streamedContent || 'Waiting for response...'}
                </pre>
              </div>
            </div>
          )}

          {/* Error Display */}
          {status === 'error' && error && (
            <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">Generation failed</p>
                <p className="text-sm text-red-600">{error}</p>
              </div>
            </div>
          )}

          {/* Result Preview */}
          {result && renderPreview && (
            <div className="rounded-lg border bg-green-50 p-4">
              {renderPreview(result)}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 flex items-center justify-between border-t px-6 py-4">
          <p className="text-xs text-muted-foreground">Powered by Amazon Bedrock</p>
          <div className="flex gap-3">
            {status === 'generating' ? (
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
            ) : (
              <Button variant="outline" onClick={handleClose}>
                Close
              </Button>
            )}

            {status === 'success' && result ? (
              <Button onClick={handleUseResult}>
                <Check className="mr-2 h-4 w-4" />
                {successLabel}
              </Button>
            ) : (
              <Button
                onClick={handleGenerate}
                disabled={(!prompt.trim() && !imageFile) || status === 'generating'}
              >
                {status === 'generating' ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-4 w-4" />
                    {submitLabel}
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
